import { GoldenLayout } from "golden-layout";
import {
  ComponentType,
  lazy,
  LazyExoticComponent,
  MemoExoticComponent,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { ChartLoadingPlaceholder } from "./components/ChartPane/ChartPane.js";
import { HeaderSummary } from "./components/HeaderSummary.js";
import { IndependentLayoutConfig } from "./components/IndependentLayoutConfig";
import { SharedLayoutConfig } from "./components/SharedLayoutConfig";
import { Toaster } from "./components/ui/sonner.js";
import { useIsMobile } from "./hooks/use-mobile.js";
import { useAppInstanceContext } from "./providers/app-instance-provider.js";

// These three pull in the heavy libraries — split into their own chunks.
const MapPane = lazy(() =>
  import("./components/MapPane/MapPane.js").then((m) => ({
    default: m.MapPane,
  })),
);
const MapControlsPane = lazy(() =>
  import("./components/MapPane/MapControlsPane.js").then((m) => ({
    default: m.MapControlsPane,
  })),
);
const MapSettingsPane = lazy(() =>
  import("./components/MapPane/MapSettingsPane.js").then((m) => ({
    default: m.MapSettings,
  })),
);
const TablePane = lazy(() =>
  import("./components/TablePane.js").then((m) => ({ default: m.TablePane })),
);
const ChartPane = lazy(() =>
  import("./components/ChartPane/ChartPane.js").then((m) => ({
    default: m.ChartPane,
  })),
);
const QueryBuilderPane = lazy(() =>
  import("./components/QueryBuilderPane.js").then((m) => ({
    default: m.QueryBuilderPane,
  })),
);

const MapNavigationPanel = lazy(
  () => import("./components/MapNavigationPanel.js"),
);

const MapLegend = lazy(() =>
  import("./components/MapPane/MapLegend.js").then((m) => ({
    default: m.MapLegend,
  })),
);

const AdvancedDownloadPane = lazy(() =>
  import("./components/AdvancedDownloadPane.js").then((m) => ({
    default: m.AdvancedDownloadPane,
  })),
);

type WorkspacePane =
  | "queryBuilder"
  | "mapControls"
  | "mapSettings"
  | "map"
  | "table"
  | "advancedDownload"
  | "legend"
  | "chart"
  | "mapNavigationPanel"
  | "header_summary";

interface PaneEntry {
  id: string;
  containerEl: HTMLElement;
  Component: ComponentType<any> | LazyExoticComponent<ComponentType<any>>;
  ComponentState: Record<string, unknown>;
  // Tracks whether this pane's tab is the active one within its
  // GoldenLayout stack (tabbed group). Starts true — a pane not in any
  // stack is always visible and never fires 'hide', and a pane that IS
  // in a stack but happens to start as the active tab should render
  // immediately rather than waiting for a 'show' event that may not
  // fire again until the user switches away and back.
  isVisible: boolean;
}

export function ExplorerApp({
  view,
  onQueryHelpClick,
}: {
  view: "shared" | "independent";
  onQueryHelpClick: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [panes, setPanes] = useState<PaneEntry[]>([]);
  const layoutRef = useRef<GoldenLayout | null>(null);
  const paneCounterRef = useRef<number>(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;

    const layout = new GoldenLayout(host);
    layoutRef.current = layout;
    let disposing = false;

    const registerPane = (
      type: WorkspacePane,
      Component: LazyExoticComponent<MemoExoticComponent<() => ReactElement>>,
    ) => {
      layout.registerComponentFactoryFunction(type, (container, state) => {
        const id = `${type}-${++paneCounterRef.current}`;
        const portalTarget = document.createElement("div");
        portalTarget.className =
          "gl-react-host h-full w-full min-h-0 min-w-0 overflow-auto";
        portalTarget.role = "tab";
        container.element.appendChild(portalTarget);

        setPanes((prev) => [
          ...prev,
          {
            id,
            containerEl: portalTarget,
            Component,
            ComponentState: (state ?? {}) as Record<string, unknown>,
            isVisible: true,
          },
        ]);

        // GoldenLayout fires 'show'/'hide' on a container specifically
        // when its tab becomes the active/inactive one within a stack
        // (tabbed group). A pane not in any stack is always visible and
        // never fires 'hide' at all, so this naturally only affects
        // stacked panes without needing to special-case them.
        container.on("show", () => {
          setPanes((prev) =>
            prev.map((p) => (p.id === id ? { ...p, isVisible: true } : p)),
          );
        });

        container.on("hide", () => {
          setPanes((prev) =>
            prev.map((p) => (p.id === id ? { ...p, isVisible: false } : p)),
          );
        });

        container.on("beforeComponentRelease", () => {
          if (disposing) {
            return;
          }

          flushSync(() => {
            setPanes((prev) => prev.filter((p) => p.id !== id));
          });
        });

        container.on("destroy", () => {});
      });
    };

    registerPane("queryBuilder", QueryBuilderPane);
    registerPane("mapSettings", MapSettingsPane);
    registerPane("mapControls", MapControlsPane);
    registerPane("map", MapPane);
    registerPane("table", TablePane);
    registerPane("advancedDownload", AdvancedDownloadPane);
    registerPane(
      "chart",
      ChartPane as LazyExoticComponent<MemoExoticComponent<() => ReactElement>>,
    );
    registerPane("legend", MapLegend);
    registerPane("mapNavigationPanel", MapNavigationPanel);

    layout.on("tabCreated", (tab) => {
      if (tab?.componentItem?.componentType == "queryBuilder") {
        const helpBtn = document.createElement("button");
        helpBtn.className = "query-builder-help";
        helpBtn.title = "Query Builder Help";

        const icon = document.createElement("i");
        icon.className = "fa-solid fa-circle-question";

        helpBtn.appendChild(icon);

        helpBtn.addEventListener("click", (e) => {
          e?.preventDefault();
          e?.stopPropagation();
          onQueryHelpClick();
        });

        tab.titleElement.appendChild(helpBtn);
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      layout.updateRootSize();
    });
    resizeObserver.observe(host);

    return () => {
      disposing = true;
      resizeObserver.disconnect();
      if (layoutRef.current == layout) {
        layoutRef.current = null;
      }
      layout.destroy();
    };
  }, []);

  useEffect(() => {
    const layout = layoutRef.current;
    if (!layout) return;
    const frameId = requestAnimationFrame(() => {
      const activeTab = localStorage.getItem("tableActiveTab");

      if (activeTab != null) {
        localStorage.removeItem("tableActiveTab");
      }

      layout.loadLayout(
        view === "independent" && !isMobile
          ? IndependentLayoutConfig(
              activeTab === "advancedDowload" ? true : false,
            )
          : SharedLayoutConfig(isMobile),
      );
      layout.updateRootSize();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [view, isMobile]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <HeaderSummary />
      <div
        className="w-full min-h-0 min-w-0 flex-1 overflow-clip"
        ref={containerRef}
        role="tablist"
      >
        {panes.map(
          ({ id, containerEl, Component, ComponentState, isVisible }) => isVisible && 
            createPortal(
              <Suspense
                fallback={
                  <ChartLoadingPlaceholder
                    title=""
                    label=""
                  ></ChartLoadingPlaceholder>
                }
              >
                <Component {...ComponentState} />
              </Suspense>,
              containerEl,
              id,
            ),
        )}
      </div>
      <Toaster />
    </div>
  );
}
